import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import KPITile from "@/components/shared/KPITile";
import SeverityBadge from "@/components/shared/SeverityBadge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Ambulance, Clock, HeartPulse, Activity, AlertTriangle, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { extractApiError } from "@/lib/api";
import { downloadReport } from "@/lib/downloadReport";
import { fetchIncidentStatusCounts, getByStatusCount } from "@/lib/incidentStatusCounts";
import { useIncidentSocket } from "@/hooks/useIncidentSocket";
import LocationLabel from "@/components/shared/LocationLabel";

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function formatIncidentType(value) {
  return String(value || "Incident")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeSeverity(value) {
  const severity = String(value || "low");
  return ["critical", "high", "medium", "low"].includes(severity) ? severity : "low";
}

function formatTime(value) {
  if (!value) return "—";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function mapIncident(item) {
  return {
    id: item?.id || item?.incident_id || "",
    status: String(item?.status || "OPEN").toUpperCase(),
    type: formatIncidentType(item?.incident_type || item?.type),
    severity: normalizeSeverity(item?.severity),
    locationRaw: item?.location ?? item?.address ?? item?.place ?? null,
    location:
      item?.location?.address ||
      item?.location ||
      item?.address ||
      item?.place ||
      [item?.city, item?.state].filter(Boolean).join(", ") ||
      "Location unavailable",
    timestamp: item?.event_time || item?.created_at || item?.updated_at || item?.timestamp || "",
  };
}

export default function AmbulanceDashboard() {
  const navigate = useNavigate();
  const [openIncidents, setOpenIncidents] = useState([]);
  const [openIncidentsCount, setOpenIncidentsCount] = useState(0);
  const [activeTripsCount, setActiveTripsCount] = useState(0);
  const [analyticsTripsCount, setAnalyticsTripsCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [liveCount, setLiveCount] = useState(0);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [statusCounts, previewResp, tripResp] = await Promise.all([
        fetchIncidentStatusCounts(),
        api.get("/incidents", { params: { status: "OPEN", page_size: 5 } }),
        api.get("/trips", { params: { status: "ACTIVE", page_size: 1 } }),
      ]);

      const previewData = previewResp.data || {};
      const previewList = normalizeCollection(previewData).map(mapIncident).filter((item) => item.id);

      setOpenIncidents(previewList);
  setOpenIncidentsCount(getByStatusCount(statusCounts?.by_status || {}, "OPEN"));
      setActiveTripsCount(tripResp.data?.meta?.total ?? 0);
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to load ambulance dashboard.");
      setOpenIncidents([]);
      setOpenIncidentsCount(0);
      setActiveTripsCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    let isMounted = true;

    const fetchTripsAnalytics = async () => {
      try {
        const response = await api.get("/analytics/trips/summary", { params: { period: "7d" } });
        if (!isMounted) return;

        const trips = response?.data?.data?.trips;
        setAnalyticsTripsCount(Number.isFinite(Number(trips?.value)) ? Number(trips.value) : null);
      } catch (err) {
        if (!isMounted) return;
        const { message } = extractApiError(err);
        console.warn("[AmbulanceDashboard] Failed to load analytics trips summary:", message || err?.message || err);
        setAnalyticsTripsCount(null);
      }
    };

    fetchTripsAnalytics();

    return () => {
      isMounted = false;
    };
  }, []);

  useIncidentSocket((incidentData) => {
    const incident = mapIncident(incidentData);
    if (!incident.id) return;

    setOpenIncidents((prev) => {
      const exists = prev.some((item) => item.id === incident.id);
      if (incident.status !== "OPEN") {
        if (!exists) return prev;
        setOpenIncidentsCount((count) => Math.max(0, count - 1));
        return prev.filter((item) => item.id !== incident.id);
      }
      if (exists) {
        return prev.map((item) => (item.id === incident.id ? incident : item));
      }
      setLiveCount((count) => count + 1);
      setOpenIncidentsCount((count) => count + 1);
      return [incident, ...prev].slice(0, 5);
    });
  });

  const reportRows = useMemo(
    () => [
      ["Incoming Alerts", openIncidentsCount],
      ["Active Trips", activeTripsCount],
      ["Live Alert Pushes", liveCount],
    ],
    [openIncidentsCount, activeTripsCount, liveCount],
  );

  return (
    <div className="space-y-6" data-testid="ambulance-dashboard">
      <PageHeader
        title="Dashboard"
        subtitle="108 Dispatch · Operational overview with live alert preview."
        onDownload={() => {
          downloadReport({
            filename: "ambulance_dashboard",
            title: "Ambulance Dashboard Summary",
            headers: ["Metric", "Value"],
            rows: reportRows,
          });
          toast.success("Report downloaded");
        }}
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
              Live +{liveCount}
            </Badge>
            <Button size="sm" variant="outline" onClick={fetchDashboardData} disabled={loading}>
              {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />}
              Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/ambulance/active")}>
              <Ambulance size={14} className="mr-1" /> Active Trips
            </Button>
          </div>
        }
      />

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="rounded-2xl border border-stone-200 bg-white p-5 text-slate-900 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-stone-100">
            <HeartPulse size={28} strokeWidth={1.8} />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold">Operational pages are live</h2>
            {/* <p className="mt-1 text-sm text-slate-500">Incoming Alerts and Active Trips are connected to backend data. Analytics widgets show 0 until backend metrics are added.</p> */}
          </div>
          <span className="rounded-full bg-stone-100 px-4 py-1.5 text-xs font-semibold tracking-wide text-slate-700">Operational</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <KPITile icon={AlertTriangle} label="Incoming alerts" value={String(openIncidentsCount)} accent="rose" />
        <KPITile icon={Ambulance} label="Active trips" value={String(activeTripsCount)} accent="amber" />
        <KPITile icon={Activity} label="Live pushes" value={String(liveCount)} accent="blue" />
        <KPITile
          icon={Clock}
          label="Analytics"
          value={Number.isFinite(Number(analyticsTripsCount)) ? Number(analyticsTripsCount).toLocaleString() : "—"}
          accent="slate"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-5 xl:col-span-2">
          <SectionCard title="Recent Alerts" subtitle="Preview of open incidents routed to ambulance services.">
            {loading && openIncidents.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-slate-500">
                <Loader2 size={18} className="mr-2 animate-spin" /> Loading alerts...
              </div>
            ) : openIncidents.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">No open incidents.</p>
            ) : (
              <div className="space-y-0">
                {openIncidents.map((incident) => (
                  <button
                    key={incident.id}
                    type="button"
                    onClick={() => navigate("/ambulance/alerts")}
                    className="flex w-full items-center gap-3 border-b border-slate-100 py-3 text-left last:border-b-0"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
                      <AlertTriangle size={15} strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900">{incident.type}</p>
                      <LocationLabel locationValue={incident.locationRaw ?? incident.location} className="truncate text-[11px] text-slate-500" />
                      <p className="text-[11px] text-slate-400">{formatTime(incident.timestamp)}</p>
                    </div>
                    <SeverityBadge severity={incident.severity} />
                  </button>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Operational Focus" subtitle="Use the dedicated pages for real-time dispatch work.">
            <div className="grid gap-3 md:grid-cols-2">
              <button onClick={() => navigate("/ambulance/alerts")} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 text-left hover:bg-slate-50">
                <AlertTriangle size={18} className="text-amber-500" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">Incoming Alerts</p>
                  <p className="text-xs text-slate-500">Accept incidents, dispatch, and log service responses.</p>
                </div>
                <ChevronRight size={14} className="text-slate-400" />
              </button>
              <button onClick={() => navigate("/ambulance/active")} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-4 text-left hover:bg-slate-50">
                <Ambulance size={18} className="text-blue-500" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">Active Trips</p>
                  <p className="text-xs text-slate-500">Track live vehicle position and speed for active transports.</p>
                </div>
                <ChevronRight size={14} className="text-slate-400" />
              </button>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-5">
          <SectionCard title="Counts">
            <div className="space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="flex items-center gap-2"><AlertTriangle size={14} className="text-rose-500" /> Incoming alerts</span>
                <span className="font-bold">{openIncidentsCount}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="flex items-center gap-2"><Ambulance size={14} className="text-amber-500" /> Active trips</span>
                <span className="font-bold">{activeTripsCount}</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="flex items-center gap-2"><Activity size={14} className="text-blue-500" /> Live incident pushes</span>
                <span className="font-bold">{liveCount}</span>
              </div>
            </div>
          </SectionCard>

          {/* <SectionCard title="Next Steps" subtitle="Dashboard notes.">
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="rounded-xl bg-slate-50 px-4 py-3">KPI trends and crew availability show 0 until backend metrics are added.</li>
              <li className="rounded-xl bg-slate-50 px-4 py-3">Use completed trips reporting on the dedicated page.</li>
              <li className="rounded-xl bg-slate-50 px-4 py-3">Use the alerts page for operational response logging.</li>
            </ul>
          </SectionCard> */}
        </div>
      </div>
    </div>
  );
}
